// src/components/antenna/antenna-recommended-actions.tsx
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Link2,
  MapPin,
  FileText,
  ExternalLink,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Presentation,
  Sparkles,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { buildApiUrl } from "@/api/url-route";
import {
  apiFetch,
  AuthExpiredError,
  getStoredToken,
  notifyAuthExpired,
} from "@/api/api-fetch";

import { getModelLabel, getModelColor } from "@/app/constants/models";
import { LockedFeatureModal } from "../navigation/locked-feature-modal";
import { Button } from "../kit";

// ─── Action Items gate ──────────────────────────────────────────────────────────
// Action Items tooling is not exposed yet. While this is false, the card's
// Implement / View-in-log buttons open a coming-soon modal instead of routing
// into the (not-yet-available) Action Items module. Flip to true to restore the
// real behavior in one line.
const ACTION_ITEMS_ENABLED = true;

const ACTION_ITEMS_LOCK_COPY = {
  title: "Action Items is almost here",
  desc:
    "This is where a recommendation becomes a tracked task: see exactly what to change and where, mark it done, and watch your score respond. We're putting the finishing touches on it now.",
  highlights: [
    "Send any recommendation straight to your task list",
    "Step-by-step fix instructions with sources",
    "Track what's shipped and what's still open",
  ],
  cta: "Got it",
  toast: "Action Items is still in the works. It will unlock here automatically when it ships.",
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActionReference {
  label: string;
  url?: string | null;
  excerpt: string;
}

interface ActionItem {
  id: string | number;
  title: string;
  description: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  impact: number;
  effort: string;
  /** Provider that generated this action: "openai" | "claude" | "perplexity" | "gemini" */
  modelSource?: string;
  sourceIssue?: string;
  whereToUpdate?: string[];
  updateDetails?: string[];
  references?: ActionReference[];
}

// ─── Inline API helpers ───────────────────────────────────────────────────────
// apiPost was an inline reimplementation of @/api/api-fetch with weaker error
// parsing and the empty-bearer bug. It now delegates to the shared client so
// 401s here also drop the user out via the global auth:expired interceptor.

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
}

async function downloadBlob(apiPath: string, filename: string): Promise<void> {
  const base  = buildApiUrl();
  const token = getStoredToken();
  if (!token) {
    notifyAuthExpired(apiPath);
    throw new AuthExpiredError(apiPath);
  }
  const res = await fetch(`${base}${apiPath}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    notifyAuthExpired(apiPath);
    throw new AuthExpiredError(apiPath);
  }
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const obj  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: obj, download: filename });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(obj), 5000);
}

function downloadCSV(actions: ActionItem[], brandName: string): void {
  const q    = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["ID", "Priority", "Model", "Title", "Description", "Impact", "Effort", "Source Issue", "Where To Update"];
  const rows = actions.map((a) => [
    q(a.id),
    q(a.priority),
    q(getModelLabel(a.modelSource || "", "AI")),
    q(a.title),
    q(a.description),
    q(a.impact),
    q(a.effort),
    q((a as any).sourceIssue ?? ""),
    q(((a as any).whereToUpdate ?? []).join("; ")),
  ]);
  const csv  = [head.map(q).join(","), ...rows.map((r) => r.join(","))].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const obj  = URL.createObjectURL(blob);
  const safe = brandName.replace(/\s+/g, "_").slice(0, 40);
  const a    = Object.assign(document.createElement("a"), { href: obj, download: `actions_${safe}.csv` });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(obj), 5000);
}

// ─── Top-N selection with model diversity ────────────────────────────────────
// Picks the top `count` actions in their existing (priority/impact) order, but
// if they'd all come from a single model, swaps the lowest-ranked slot for the
// next available action from a different model so at least 2 models are
// represented among the top picks.

function selectTopWithDiversity(actions: ActionItem[], count: number): ActionItem[] {
  if (actions.length <= count) return actions;

  const top = actions.slice(0, count);
  const usedModels = new Set(top.map((a) => a.modelSource || ""));
  if (usedModels.size >= 2) return top;

  const altIndex = actions.findIndex(
    (a, i) => i >= count && a.modelSource && a.modelSource !== top[0].modelSource,
  );
  if (altIndex === -1) return top; // no other model available in the full list

  const result = [...top];
  result[count - 1] = actions[altIndex];
  return result;
}

// ─── Export dropdown — recommendations only ──────────────────────────────────
// Full-report exports (PDF / PPTX) live on the header now. This menu is scoped
// strictly to outputs derived from the Recommendations list.

function ExportMenu({
  runId,
  brandName,
  allActions,
  disabled,
}: {
  runId?: number;
  brandName: string;
  allActions: ActionItem[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const safe            = brandName.replace(/\s+/g, "_").slice(0, 40);

  const handle = async (key: string, fn: () => Promise<void> | void) => {
    setBusy(key);
    setOpen(false);
    try { await fn(); toast.success("Export ready"); }
    catch (e: any) { console.error("Export failed", e); toast.error("Export failed. Please try again or contact support."); }
    finally { setBusy(null); }
  };

  return (
    <div className="relative">
      <Button
        variant="secondary"
        disabled={disabled || !!busy}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        Export
        <ChevronDown className="w-3 h-3" />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-20 rounded-xl border shadow-xl overflow-hidden"
            style={{ backgroundColor: "var(--card)", borderColor: "var(--border)", minWidth: 280 }}
          >
            {/* Recommendations-only PPTX */}
            {runId && (
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
                onClick={() =>
                  handle(
                    "actions-pptx",
                    () => downloadBlob(
                      `/monitoring/runs/${runId}/export/actions/pptx`,
                      `actions_${safe}_${runId}.pptx`,
                    ),
                  )
                }
              >
                <Presentation className="w-4 h-4 flex-shrink-0" style={{ color: "var(--spectral-violet)" }} />
                <div>
                  <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Recommendations (.pptx)</div>
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                    Actions-only slide deck
                  </div>
                </div>
              </button>
            )}

            {/* CSV — always available, works off the in-memory actions list */}
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
              onClick={() => handle("csv", () => downloadCSV(allActions, brandName))}
            >
              <FileSpreadsheet className="w-4 h-4 flex-shrink-0" style={{ color: "var(--chart-3)" }} />
              <div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>All Recommendations (.csv)</div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  {allActions.length} actions as spreadsheet
                </div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Single action card ───────────────────────────────────────────────────────

function ActionCard({
  action,
  index,
  isIngested,
  onImplementAction,
  onViewInActionLog,
}: {
  action: ActionItem;
  index: number;
  isIngested: boolean;
  onImplementAction?: (action: ActionItem) => void;
  onViewInActionLog?: (action: ActionItem) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered]   = useState(false);

  const raw           = action as any;
  const whereToUpdate = Array.isArray(raw.whereToUpdate) ? (raw.whereToUpdate as string[]) : [];
  const updateDetails = Array.isArray(raw.updateDetails) ? (raw.updateDetails as string[]) : [];
  const references    = Array.isArray(raw.references)    ? (raw.references    as ActionReference[]) : [];
  const sourceIssue   = typeof raw.sourceIssue === "string" ? raw.sourceIssue.trim() : "";
  const impactNum     = typeof action.impact === "number" ? action.impact : Number(action.impact ?? 0);
  const effortStr     = action.effort || "Medium";
  const hasDetails    = !!sourceIssue || whereToUpdate.length > 0 || updateDetails.length > 0 || references.length > 0;

  const pr = (action.priority || "LOW").toUpperCase();
  const priorityStyle =
    pr === "HIGH"
      ? { bg: "var(--infrared-coral-10)", border: "var(--infrared-coral-20)", text: "var(--infrared-coral)", label: "High" }
      : pr === "MEDIUM"
      ? { bg: "var(--warning-bg)", border: "var(--warning-border)", text: "var(--warning-text)", label: "Medium" }
      : { bg: "var(--success-bg)", border: "var(--success-border)", text: "var(--success-text)", label: "Low" };

  const impactColor =
    impactNum >= 80 ? "var(--success-text)" :
    impactNum >= 50 ? "var(--warning-text)" :
                      "var(--foreground)";

  const effortFilled = /low/i.test(effortStr) ? 1 : /high/i.test(effortStr) ? 3 : 2;

  const srcLabel = action.modelSource ? getModelLabel(action.modelSource, "AI") : "";
  const MODEL_DOT: Record<string, string> = {
    ChatGPT: "var(--chart-3)", Claude: "var(--chart-1)", Gemini: "var(--chart-2)", Perplexity: "var(--chart-4)",
  };
  const modelDot = MODEL_DOT[srcLabel] ?? "var(--muted-foreground)";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        gap: 18,
        border: `1px solid ${isIngested ? "var(--alpha-teal-20)" : "var(--border)"}`,
        borderRadius: 14,
        background: "var(--card)",
        padding: "18px 20px",
        boxShadow: hovered ? "0 12px 30px -14px rgba(13,13,15,.18)" : "0 1px 2px rgba(13,13,15,.03)",
        transform: hovered ? "translateY(-1px)" : "none",
        transition: "box-shadow var(--duration-base) var(--ease-standard), transform var(--duration-base) var(--ease-standard)",
      }}
    >
      {/* Index number */}
      <div style={{ flexShrink: 0, fontSize: 30, fontWeight: 700, lineHeight: 1, color: "var(--silver-mist)", fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em" }}>
        {String(index + 1).padStart(2, "0")}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 9 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: "var(--foreground)", flex: 1, minWidth: 0 }}>
            {action.title}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {isIngested && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, backgroundColor: "var(--alpha-teal-10)", border: "1px solid var(--alpha-teal-20)", color: "var(--alpha-teal)" }}>
                <CheckCircle2 className="w-3 h-3" /> In Action Log
              </span>
            )}
            <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, backgroundColor: priorityStyle.bg, border: `1px solid ${priorityStyle.border}`, color: priorityStyle.text }}>
              {priorityStyle.label}&nbsp;priority
            </span>
          </div>
        </div>

        {/* Description */}
        <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--muted-foreground)", margin: "0 0 16px" }}>
          {action.description}
        </p>

        {/* ── Footer: impact · effort · model · implement ───────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          {/* Impact meter */}
          <div style={{ width: 132, flexShrink: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted-foreground)" }}>Impact</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: impactColor, fontVariantNumeric: "tabular-nums" }}>{impactNum}</span>
            </div>
            <div style={{ height: 6, borderRadius: 9999, background: "var(--muted)", overflow: "hidden" }}>
              <div style={{ width: `${Math.max(0, Math.min(100, impactNum))}%`, height: "100%", borderRadius: 9999, background: "linear-gradient(90deg, var(--alpha-teal), var(--spectral-violet))" }} />
            </div>
          </div>

          {/* Effort */}
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted-foreground)" }}>Effort</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[0, 1, 2].map((d) => (
                <span key={d} style={{ width: 7, height: 7, borderRadius: 9999, backgroundColor: d < effortFilled ? "var(--foreground)" : "var(--silver-mist)" }} />
              ))}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--foreground)" }}>{effortStr}</span>
          </div>

          {/* Model attribution */}
          {srcLabel && (
            <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted-foreground)" }}>
              <span style={{ width: 6, height: 6, borderRadius: 9999, backgroundColor: modelDot }} />
              via {srcLabel}
            </span>
          )}

          {/* Implement / View in log */}
          {isIngested ? (
            ACTION_ITEMS_ENABLED ? (
              <button
                onClick={() => onViewInActionLog?.(action)}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 15px", borderRadius: 8, border: "1px solid var(--alpha-teal-30)", background: "var(--alpha-teal-5)", color: "var(--alpha-teal)", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: srcLabel ? 0 : "auto" }}
              >
                <ExternalLink size={14} /> View in Action Log
              </button>
            ) : (
              <button
                onClick={() => onViewInActionLog?.(action)}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 15px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--muted-10)", color: "var(--muted-foreground)", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: srcLabel ? 0 : "auto" }}
              >
                Action Log (coming soon)
              </button>
            )
          ) : ACTION_ITEMS_ENABLED ? (
            <button
              onClick={() => onImplementAction?.(action)}
              onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 15px", borderRadius: 8, border: "none", background: "var(--alpha-teal)", color: "#04312F", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: srcLabel ? 0 : "auto" }}
            >
              Implement action <ArrowRight size={14} color="#04312F" />
            </button>
          ) : (
            <button
              onClick={() => onImplementAction?.(action)}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 34, padding: "0 15px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--muted-10)", color: "var(--muted-foreground)", fontSize: 13, fontWeight: 600, cursor: "pointer", marginLeft: srcLabel ? 0 : "auto" }}
            >
              Implement action (coming soon)
            </button>
          )}
        </div>

        {/* View details toggle */}
        {hasDetails && (
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted-foreground)", fontSize: 12, fontWeight: 600 }}
          >
            {expanded ? "Hide details" : "View details"}
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}

        {/* ── Expanded details ──────────────────────────────────────── */}
        {expanded && (
          <div className="mt-4 space-y-3">
            {sourceIssue && (
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: "var(--muted-10)", border: "1px solid var(--border)" }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <FileText className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
                    Source Issue
                  </span>
                </div>
                <p style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--foreground)" }}>{sourceIssue}</p>
              </div>
            )}

            {whereToUpdate.length > 0 && (
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />
                  <h5 style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Where to update</h5>
                </div>
                <ul className="space-y-1.5">
                  {whereToUpdate.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }}>•</span>
                      <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {updateDetails.length > 0 && (
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)" }}
              >
                <h5 className="mb-2" style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>What needs to change</h5>
                <ul className="space-y-1.5">
                  {updateDetails.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }}>•</span>
                      <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {references.length > 0 && (
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />
                  <h5 style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>References</h5>
                </div>
                <div className="space-y-2">
                  {references.map((ref, idx) => (
                    <div
                      key={idx}
                      className="p-2.5 rounded-lg"
                      style={{ backgroundColor: "var(--muted-10)", border: "1px solid var(--border)" }}
                    >
                      <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, marginBottom: 3 }}>
                        {ref.url
                          ? <a href={ref.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>{ref.label}</a>
                          : ref.label}
                      </div>
                      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.5 }}>{ref.excerpt}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Model legend ─────────────────────────────────────────────────────────────

function ModelLegend({ actions }: { actions: ActionItem[] }) {
  const sources = useMemo(() => {
    const seen = new Map<string, string>();
    actions.forEach((a) => {
      if (a.modelSource && !seen.has(a.modelSource)) {
        seen.set(a.modelSource, getModelLabel(a.modelSource, "AI"));
      }
    });
    return Array.from(seen.entries());
  }, [actions]);

  if (sources.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Sources:</span>
      {sources.map(([key, label]) => {
        const c = getModelColor(key);
        return (
          <span
            key={key}
            style={{
              display:         "inline-flex",
              alignItems:      "center",
              padding:         "2px 8px",
              borderRadius:    9999,
              backgroundColor: `${c}15`,
              border:          `1px solid ${c}40`,
              color:           c,
              fontSize:        "var(--text-xs)",
              fontWeight:      600,
            }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AntennaRecommendedActionsProps {
  recommendedActions: any[];
  runId?: number;
  brandName?: string;
  selectedModels?: string[];
  ingestedSourceIds?: Set<string>;
  onImplementAction?: (action: ActionItem) => void;
  onViewInActionLog?: (action: ActionItem) => void;
}

export function AntennaRecommendedActions({
  recommendedActions,
  runId,
  brandName = "",
  selectedModels = [],
  ingestedSourceIds,
  onImplementAction,
  onViewInActionLog,
}: AntennaRecommendedActionsProps) {
  const [allActions, setAllActions]       = useState<ActionItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showAll, setShowAll]             = useState(false);
  const [showActionLock, setShowActionLock] = useState(false);

  const TOP_COUNT = 3;

  // While Action Items is gated off, both card buttons open the coming-soon
  // modal instead of writing to / navigating into the Action Items module.
  const implementHandler = ACTION_ITEMS_ENABLED
    ? onImplementAction
    : () => setShowActionLock(true);
  const viewInLogHandler = ACTION_ITEMS_ENABLED
    ? onViewInActionLog
    : () => setShowActionLock(true);

  const topActions = useMemo(
    () => selectTopWithDiversity(allActions, TOP_COUNT),
    [allActions],
  );

  useEffect(() => {
    setAllActions(
      Array.isArray(recommendedActions) ? (recommendedActions as ActionItem[]) : [],
    );
  }, [recommendedActions]);

  const ingestedCount = ingestedSourceIds?.size ?? 0;

  const handleLoadMore = useCallback(async () => {
    if (!runId) { toast.error("Run ID not available."); return; }
    setIsLoadingMore(true);
    try {
      const existingTitles = allActions.map((a) => a.title);
      const lastId =
        typeof allActions[allActions.length - 1]?.id === "number"
          ? (allActions[allActions.length - 1].id as number) + 1
          : 1000;

      const result = await apiPost<{ actions: ActionItem[]; count: number }>(
        `/monitoring/runs/${runId}/actions/more`,
        {
          brand:          brandName,
          models:         (selectedModels || []).slice(0, 2),
          existingTitles,
          startId:        lastId,
          count:          2,
        },
      );

      if (!result.actions?.length) {
        toast.info("No additional unique recommendations found.");
        return;
      }
      setAllActions((prev) => [...prev, ...result.actions]);
      toast.success(
        `${result.actions.length} new recommendation${result.actions.length !== 1 ? "s" : ""} added`,
      );
    } catch (e: any) {
      console.error("Failed to load more recommendations", e);
      toast.error("Couldn't load more recommendations. Please try again.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [runId, brandName, selectedModels, allActions]);

  return (
    <div
      className="rounded-2xl"
      style={{
        backgroundColor: "var(--card)",
        boxShadow:       "0 1px 2px rgba(13,13,15,.04), 0 18px 44px -20px rgba(13,13,15,.16)",
        border:          "1px solid var(--border)",
        position:        "relative",
        overflow:        "hidden",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0"
        style={{
          height:     3,
          background: "linear-gradient(90deg, var(--alpha-teal), var(--spectral-violet))",
        }}
      />

      <div className="p-8 pt-9 space-y-6">

        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-.01em", color: "var(--foreground)" }}>
                {showAll ? "Recommended actions" : "Top recommended actions"}
              </h2>

              {ingestedCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "var(--alpha-teal-10)",
                    color:           "var(--alpha-teal)",
                    border:          "1px solid var(--alpha-teal-20)",
                    fontSize:        "var(--text-xs)",
                    fontWeight:      600,
                  }}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {ingestedCount} in Action Log
                </span>
              )}
            </div>

            <p style={{ color: "var(--muted-foreground)", marginTop: 4, fontSize: "var(--text-sm)" }}>
              Execution-ready actions with update locations and supporting references
            </p>

            <div className="mt-2">
              <ModelLegend actions={allActions} />
            </div>
          </div>

          <div className="flex-shrink-0">
            <ExportMenu
              runId={runId}
              brandName={brandName}
              allActions={allActions}
              disabled={isLoadingMore}
            />
          </div>
        </div>

        {allActions.length === 0 ? (
          <div
            className="flex items-center justify-center p-10 rounded-xl border"
            style={{ backgroundColor: "var(--muted-10)", borderColor: "var(--border)" }}
          >
            <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>
              No recommendations available.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(showAll ? allActions : topActions).map((action, i) => {
              const ingested = ingestedSourceIds?.has(String(action.id)) ?? false;
              return (
                <ActionCard
                  key={action.id}
                  action={action}
                  index={i}
                  isIngested={ingested}
                  onImplementAction={implementHandler}
                  onViewInActionLog={viewInLogHandler}
                />
              );
            })}
          </div>
        )}

        {allActions.length > TOP_COUNT && (
          <div className="pt-2 flex justify-center">
            <button
              onClick={() => setShowAll((prev) => !prev)}
              className="flex items-center gap-1"
              style={{
                fontSize:   "var(--text-sm)",
                color:      "var(--alpha-teal)",
                fontWeight: 600,
                background: "none",
                border:     "none",
                cursor:     "pointer",
                padding:    0,
              }}
            >
              {showAll
                ? "Show top 3 priorities"
                : `Show all ${allActions.length} recommendations`}
            </button>
          </div>
        )}

        {showAll && allActions.length > 0 && runId && (
          <div className="pt-4 flex justify-center">
            <Button
              variant="secondary"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="flex items-center gap-2"
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Finding unique recommendations…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Load more recommendations
                </>
              )}
            </Button>
          </div>
        )}

        {isLoadingMore && (
          <p
            className="text-center"
            style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}
          >
            Checking for unique recommendations not already in your list…
          </p>
        )}
      </div>

      {showActionLock && (
        <LockedFeatureModal
          feature="action-items"
          title={ACTION_ITEMS_LOCK_COPY.title}
          desc={ACTION_ITEMS_LOCK_COPY.desc}
          highlights={ACTION_ITEMS_LOCK_COPY.highlights}
          ctaLabel={ACTION_ITEMS_LOCK_COPY.cta}
          toastMessage={ACTION_ITEMS_LOCK_COPY.toast}
          onClose={() => setShowActionLock(false)}
        />
      )}
    </div>
  );
}